<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Classes Management
                </h1>
                <div class="flex space-x-3">
                    <button
                        @click="showStatistics = !showStatistics"
                        class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Statistics
                    </button>
                    <button
                        @click="openAddModal"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                        <Plus class="w-4 h-4 mr-1" />
                        Add Class
                    </button>
                </div>
            </div>

            <!-- Statistics Section -->
            <div v-if="showStatistics && statistics" class="mb-6">
                <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
                >
                    <div
                        class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                    >
                        <div class="p-4">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        class="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-5 h-5 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                    >
                                        Total Classes
                                    </p>
                                    <p
                                        class="text-lg font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ statistics.total_classes }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                    >
                        <div class="p-4">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        class="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-5 h-5 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                    >
                                        Active Classes
                                    </p>
                                    <p
                                        class="text-lg font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ statistics.active_classes }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                    >
                        <div class="p-4">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        class="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-5 h-5 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-1.066 2.573c-.94-1.543-.826 3.31-2.37 2.37.996.608 2.296.07 2.572-1.065z"
                                            />
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                    >
                                        Inactive Classes
                                    </p>
                                    <p
                                        class="text-lg font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ statistics.inactive_classes }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                    >
                        <div class="p-4">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        class="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-5 h-5 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                    >
                                        Available Slots
                                    </p>
                                    <p
                                        class="text-lg font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{
                                            statistics.classes_with_available_slots
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
                    >
                        <div class="p-4">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        class="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-5 h-5 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                    >
                                        Full Classes
                                    </p>
                                    <p
                                        class="text-lg font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ statistics.full_classes }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="classStore.search"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search classes..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Grade Level Filter -->
                    <div>
                        <SearchableSelect
                            v-model="classStore.gradeLevel"
                            :options="gradeLevelOptions"
                            label="Grade Level"
                            placeholder="All Grades"
                            option-label="label"
                            option-value="value"
                            @update:model-value="classStore.fetchClasses()"
                        />
                    </div>

                    <!-- Major Filter -->
                    <div>
                        <SearchableSelect
                            v-model="classStore.majorId"
                            :options="majorOptions"
                            label="Major"
                            placeholder="All Majors"
                            option-label="name"
                            option-value="id"
                            @update:model-value="classStore.fetchClasses()"
                        />
                    </div>

                    <!-- Status Filter -->
                    <div>
                        <SearchableSelect
                            v-model="classStore.isActive"
                            :options="statusOptions"
                            label="Status"
                            placeholder="All Status"
                            option-label="label"
                            option-value="value"
                            @update:model-value="classStore.fetchClasses()"
                        />
                    </div>

                    <PageLengthSelector
                        v-model="classStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div class="flex items-center space-x-2">
                        <label class="flex items-center">
                            <input
                                v-model="classStore.withAvailableSlots"
                                @change="classStore.fetchClasses()"
                                type="checkbox"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span
                                class="ml-2 text-sm text-gray-700 dark:text-gray-300"
                                >Only show classes with available slots</span
                            >
                        </label>
                    </div>
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="classStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="classStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ classStore.error }}
                        </p>
                        <button
                            @click="classStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!classStore.loading && !classStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Code
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Name
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Grade
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Major
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Homeroom Teacher
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Available
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="classItem in classStore.classes"
                                :key="classItem.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ classItem.code || "-" }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ classItem.name }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm text-gray-900 dark:text-white"
                                    >
                                        {{ classItem.grade_level }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm text-gray-900 dark:text-white"
                                    >
                                        {{ classItem.major?.name || "-" }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm text-gray-900 dark:text-white"
                                    >
                                        {{
                                            classItem.homeroom_teacher?.user
                                                ?.name || "-"
                                        }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm text-gray-900 dark:text-white"
                                    >
                                        {{
                                            classItem.current_students_count ||
                                            0
                                        }}/{{ classItem.capacity }}
                                    </div>
                                    <div
                                        class="w-full bg-gray-200 rounded-full h-2 mt-1"
                                    >
                                        <div
                                            class="bg-blue-600 h-2 rounded-full"
                                            :style="{
                                                width:
                                                    ((classItem.current_students_count ??
                                                        0) /
                                                        classItem.capacity) *
                                                        100 +
                                                    '%',
                                            }"
                                        ></div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            classItem.is_active
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
                                        ]"
                                    >
                                        {{
                                            classItem.is_active
                                                ? "Active"
                                                : "Inactive"
                                        }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <div class="flex space-x-2">
                                        <button
                                            @click="openEditModal(classItem)"
                                            title="Edit Class"
                                            class="inline-flex items-center justify-center p-2 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors mr-2"
                                        >
                                            <Pencil class="w-4 h-4" />
                                        </button>
                                        <button
                                            @click="openDeleteModal(classItem)"
                                            title="Delete Class"
                                            class="inline-flex items-center justify-center p-2 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <Trash2 class="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="classStore.classes.length === 0"
                    class="text-center py-12"
                >
                    <School class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No classes found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new class.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openAddModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Class
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    !classStore.loading &&
                    !classStore.error &&
                    (classStore.meta?.total || 0) >
                        (classStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="classStore.setPage(classStore.page - 1)"
                        :disabled="classStore.page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <button
                        @click="classStore.setPage(classStore.page + 1)"
                        :disabled="
                            classStore.page === classStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                classStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                classStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                classStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="classStore.setPage(classStore.page - 1)"
                                :disabled="classStore.page === 1"
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in paginationPages"
                                :key="page"
                                @click="classStore.setPage(page)"
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === classStore.page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                                ]"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="classStore.setPage(classStore.page + 1)"
                                :disabled="
                                    classStore.page ===
                                    classStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Modals -->
            <ClassSchoollModal
                v-if="showModal"
                :show="showModal"
                :class-schooll="selectedClass"
                :is-editing="isEditing"
                @close="closeModal"
                @saved="handleSave"
            />

            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="'Delete Class'"
                :message="deleteMessage"
                @close="closeDeleteModal"
                @confirm="handleDelete"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useClassSchoollStore } from "@/stores/classSchoollStore";
import { useMajorStore } from "@/stores/majorStore";
import NotificationService from "@/utils/notifications";
import ClassSchoollModal from "@/components/ClassSchoollModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import type { ClassSchooll } from "@/api/classSchooll";
import { School, Pencil, Trash2, Plus, RotateCcw } from "@lucide/vue";
import SearchableSelect from "@/components/SearchableSelect.vue";

const classStore = useClassSchoollStore();
const majorStore = useMajorStore();

const showStatistics = ref(false);
const statistics = computed(() => classStore.statistics);

// Pagination computed properties

const deleteMessage = computed(() => {
    return `Are you sure you want to delete the class "${selectedClass.value?.name || ""}"? This action cannot be undone.`;
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const isEditing = ref(false);
const selectedClass = ref<ClassSchooll | null>(null);

// Options for Grade Level filter
const gradeLevelOptions = computed(() => [
    { value: "", label: "All Grades" },
    { value: "X", label: "Grade X" },
    { value: "XI", label: "Grade XI" },
    { value: "XII", label: "Grade XII" },
]);

// Options for Major filter
const majorOptions = computed(() => {
    const options = [{ id: "", name: "All Majors" }];
    majorStore.activeMajors.forEach((major: any) => {
        options.push({ id: major.id, name: major.name });
    });
    return options;
});

// Options for Status filter
const statusOptions = computed(() => [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
]);

let searchTimeout: NodeJS.Timeout;

const debouncedSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        classStore.fetchClasses();
    }, 500);
};

const paginationPages = computed(() => {
    if (!classStore.meta) return [];

    const pages = [];
    const currentPage = classStore.page;
    const lastPage = classStore.meta.last_page;

    // Show max 5 pages
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(lastPage, startPage + 4);

    // Adjust if we're near the end
    if (endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return pages;
});

const openAddModal = () => {
    selectedClass.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any potential errors when opening modal
    classStore.clearError();
    console.log("Opening Add Class modal");
};

const openEditModal = (classItem: ClassSchooll) => {
    selectedClass.value = classItem;
    isEditing.value = true;
    showModal.value = true;
    // Clear any potential errors when opening modal
    classStore.clearError();
};

const closeModal = () => {
    showModal.value = false;
    selectedClass.value = null;
};

const openDeleteModal = (classItem: ClassSchooll) => {
    selectedClass.value = classItem;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    selectedClass.value = null;
};

const handleSave = () => {
    closeModal();
    classStore.fetchClasses();
    NotificationService.success(
        isEditing.value
            ? "Class updated successfully"
            : "Class created successfully",
    );
};

const handleDelete = async () => {
    if (!selectedClass.value) return;

    try {
        await classStore.deleteClass(selectedClass.value.id);
        NotificationService.success("Class deleted successfully");
        closeDeleteModal();
    } catch (error: any) {
        NotificationService.error(error.message || "Failed to delete class");
    }
};

const handlePerPageChange = (value: number) => {
    classStore.perPage = value;
    classStore.page = 1;
    classStore.fetchClasses();
};

const hasActiveFilters = computed(() => {
    return (
        classStore.search.trim() !== "" ||
        classStore.gradeLevel !== "" ||
        classStore.majorId !== "" ||
        classStore.isActive !== null ||
        classStore.withAvailableSlots !== false ||
        classStore.page !== 1 ||
        classStore.perPage !== 10
    );
});

const resetFilters = async () => {
    // Reset store state and ensure data refetch
    await classStore.resetFilters();

    // Explicitly fetch classes to ensure table updates
    await classStore.fetchClasses();
};

// Load majors and semesters when component mounts
onMounted(() => {
    // Ensure clean state before fetching
    classStore.clearError();
    classStore.resetFilters();
    classStore.fetchClasses();
    classStore.fetchStatistics();

    // Fetch majors for dropdown
    majorStore.fetchActiveMajors();
});

// Watch for page and perPage changes
</script>
